import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Pencil, KeyRound, UserCheck, UserX, ShieldOff, ArrowLeft, Mail, Phone, Building2, Calendar, Loader2, Save,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function passwordRules(pw) {
  return {
    len: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
  };
}

export default function AdminUserDetailPage() {
  const { userId } = useParams();
  const [params, setParams] = useSearchParams();
  const openResetInitially = params.get("reset") === "1";

  const [user, setUser] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [resetOpen, setResetOpen] = useState(openResetInitially);
  const [resetForm, setResetForm] = useState({ new: "", confirm: "" });
  const [resetError, setResetError] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setUser(data.data.user);
      setAudit(data.data.audit_logs);
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/app/super-admin/users");
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (openResetInitially) {
      // strip query so refresh doesn't re-open
      const next = new URLSearchParams(params);
      next.delete("reset");
      setParams(next, { replace: true });
    }
  }, []); // eslint-disable-line

  const applyStatus = async () => {
    if (!confirm) return;
    try {
      await api.patch(`/admin/users/${userId}/status`, { account_status: confirm.nextStatus });
      toast.success(`Account status updated.`);
      setConfirm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitReset = async () => {
    setResetError("");
    if (resetForm.new !== resetForm.confirm) {
      setResetError("Passwords do not match.");
      return;
    }
    if (!Object.values(passwordRules(resetForm.new)).every(Boolean)) {
      setResetError("Password must be 8+ chars with upper, lower, digit and special.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/users/${userId}/reset-password`, { new_password: resetForm.new });
      toast.success("Password reset. Share it with the user securely.");
      setResetOpen(false);
      setResetForm({ new: "", confirm: "" });
    } catch (err) {
      setResetError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) {
    return (
      <DashboardLayout title="User details" breadcrumbs={["Platform", "Users"]}>
        <LoadingBlock label="Loading user…" />
      </DashboardLayout>
    );
  }

  const initials = (user.first_name?.[0] || "") + (user.last_name?.[0] || "");

  return (
    <DashboardLayout title={user.full_name} breadcrumbs={["Platform", "Users", user.full_name]}>
      <button onClick={() => navigate("/app/super-admin/users")} className="mb-4 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#101876]">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to users
      </button>
      <div className="grid gap-6 lg:grid-cols-3" data-testid={ADMIN.userDetailRoot}>
        <div className="lg:col-span-2 space-y-6">
          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#101876] text-lg font-black text-white">
                {initials.toUpperCase() || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">{user.role_name}</div>
                <h2 className="mt-0.5 text-2xl font-black text-slate-900" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>{user.full_name}</h2>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {user.email}</span>
                  <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {user.mobile}</span>
                  {user.organization_name && <span className="inline-flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {user.organization_name}</span>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Link to={`/app/super-admin/users/${user.id}/edit`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]"
                      data-testid={ADMIN.userDetailEdit}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Link>
                <button onClick={() => setResetOpen(true)}
                        className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0c135c]"
                        data-testid={ADMIN.userDetailReset}>
                  <KeyRound className="h-3.5 w-3.5" /> Reset password
                </button>
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[
                ["Employee ID", user.employee_id || "—"],
                ["Designation", user.designation || "—"],
                ["Department", user.department_id || "—"],
                ["Account status", <StatusBadge status={user.account_status} />],
                ["Email verified", user.email_verified ? "Yes" : "No"],
                ["Mobile verified", user.mobile_verified ? "Yes" : "No"],
                ["Last login", user.last_login_at ? new Date(user.last_login_at).toLocaleString() : "Never"],
                ["Created", new Date(user.created_at).toLocaleString()],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{k}</div>
                  <div className="mt-1 text-sm text-slate-900">{v}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-6 py-4 text-sm font-bold text-slate-900">Recent audit trail</div>
            <ul className="divide-y divide-slate-100">
              {audit.length === 0 && <li className="p-6 text-sm text-slate-500">No activity yet.</li>}
              {audit.slice(0, 20).map((a) => (
                <li key={a.id} className="flex items-start gap-3 p-4">
                  <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-slate-50 text-[#101876]">
                    <Calendar className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800">{a.action}</div>
                    <div className="text-xs text-slate-500">{a.description}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 whitespace-nowrap">
                    {new Date(a.created_at).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#329112]">Account actions</div>
            <div className="mt-4 space-y-2">
              {user.account_status !== "active" && (
                <button className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
                        onClick={() => setConfirm({ nextStatus: "active", title: `Activate ${user.full_name}?`, destructive: false })}
                        data-testid={ADMIN.userDetailActivate}>
                  <UserCheck className="h-4 w-4" /> Activate
                </button>
              )}
              {user.account_status !== "inactive" && (
                <button className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                        onClick={() => setConfirm({ nextStatus: "inactive", title: `Deactivate ${user.full_name}?`, destructive: true })}>
                  <UserX className="h-4 w-4" /> Deactivate
                </button>
              )}
              {user.account_status !== "suspended" && (
                <button className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50"
                        onClick={() => setConfirm({ nextStatus: "suspended", title: `Suspend ${user.full_name}?`, destructive: true })}
                        data-testid={ADMIN.userDetailSuspend}>
                  <ShieldOff className="h-4 w-4" /> Suspend
                </button>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-xs text-slate-500">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">User ID</div>
            <div className="mt-1 font-mono text-slate-700 break-all">{user.id}</div>
          </div>
        </aside>
      </div>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title}
        description={`This will change the account status to ${confirm?.nextStatus?.replace(/_/g, " ")}.`}
        destructive={confirm?.destructive}
        onConfirm={applyStatus}
        testKey="user-detail-confirm"
      />

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Reset password for {user.full_name}</DialogTitle></DialogHeader>
          {resetError && <Alert>{resetError}</Alert>}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">New password</Label>
              <Input type="password" value={resetForm.new} onChange={(e) => setResetForm((f) => ({ ...f, new: e.target.value }))} data-testid={ADMIN.resetDialogNew} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Confirm password</Label>
              <Input type="password" value={resetForm.confirm} onChange={(e) => setResetForm((f) => ({ ...f, confirm: e.target.value }))} data-testid={ADMIN.resetDialogConfirm} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setResetOpen(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitReset} disabled={saving}
                    className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70"
                    data-testid={ADMIN.resetDialogSubmit}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Reset password
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Alert({ children }) {
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs font-semibold text-rose-700">
      {children}
    </div>
  );
}
